import { ReactNode } from 'react';

import {
  ComponentResponse,
  ComponentTable,
  ComponentVideo,
  ComponentImage,
  ComponentInfoBox,
  ComponentEbook,
  ComponentDocument,
  ComponentAudio,
  ComponentInteractive,
  ComponentEbookInteractive,
  ComponentColumns,
  ComponentLink,
  ComponentGuidedResearch,
  ComponentMultipleChoice,
  ComponentSlideshow,
  ComponentEquationResponse,
  ComponentVocabulary,
  ComponentFillInBlanks,
  ComponentDraw,
  ComponentDiscussion,
  ComponentInteractiveResponseSequence,
  ComponentInteractiveResponseCategorise,
  ComponentRiveAnimation,
  ComponentGif,
  ComponentImageSphere,
} from './component.constants';
import type { GetActivitiesSectionsQuery } from '../lesson-builder.graphql.generated';
import {
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE,
  ACTIVITIES_COMPONENT_TYPE_INFO_BOX,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_TABLE,
  ACTIVITIES_COMPONENT_TYPE_VIDEO,
  ACTIVITIES_COMPONENT_TYPE_EBOOK,
  ACTIVITIES_COMPONENT_TYPE_DOCUMENT,
  ACTIVITIES_COMPONENT_TYPE_AUDIO,
  ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
  ACTIVITIES_COMPONENT_TYPE_LINK,
  ACTIVITIES_COMPONENT_TYPE_WEBTOOL,
  ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
  ACTIVITIES_COMPONENT_TYPE_SLIDESHOW,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_VOCABULARY,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE,
  ActivitiesContentType,
  ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION,
  ACTIVITIES_COMPONENT_TYPE_GIF,
} from 'global/constants';
import { ComponentFieldWrapper } from './components.style';
import ResponseEdit from './response/response-edit';
import ResponseSettings from './response/response-settings';
import TableEdit from './table/table-edit';
import TableSettings from './table/table-settings';
import VideoEdit from './video/video-edit';
import VideoSettings from './video/video-settings';
import AudioEdit from './audio/audio-edit';
import AudioSettings from './audio/audio-settings';
import ImageEdit from './image/image-edit';
import ImageSettings from './image/image-settings';
import InfoBoxEdit from './info-box/info-box-edit';
import InfoBoxSettings from './info-box/info-box-settings';
import EbookEdit from './ebook/ebook-edit';
import EbookSettings from './ebook/ebook-settings';
import InteractiveEbookEdit from './interactive-ebook/interactive-ebook-edit';
import InteractiveEbookSettings from './interactive-ebook/interactive-ebook-settings';
import InteractiveEdit from './interactive/interactive-edit';
import InteractiveSettings from './interactive/interactive-settings';
import DocumentEdit from './document/document-edit';
import DocumentSettings from './document/document-settings';
import ColumnsEdit from './columns/columns-edit';
import ColumnsSettings from './columns/settings/column-settings';
import LinkEdit from './link/link-edit';
import LinkSettings from './link/link-settings';
import WebToolSettings from './webtool/webtool-settings';
import WebToolEdit from './webtool/webtool-edit';
import GuidedResearchSettings from './guided-research/guided-research-settings';
import GuidedResearchEdit from './guided-research/guided-research-edit';
import MultipleChoiceEdit from './multiple-choice/multiple-choice-edit';
import MultipleChoiceSettings from './multiple-choice/settings/multiple-choice-settings';
import SlideshowEdit from './slideshow/slideshow-edit';
import SlideshowSettings from './slideshow/slideshow-settings';
import EquationResponseEdit from './equation-response/equation-response-edit';
import EquationResponseSettings from './equation-response/equation-response-settings';
import VocabularyEdit from './vocabulary/vocabulary-edit';
import VocabularySettings from './vocabulary/settings/vocabulary-settings';
import FillInBlanksEdit from './fill-in-blanks/fill-in-blanks-edit';
import FillInBlanksSettings from './fill-in-blanks/settings/fill-in-blanks-settings';
import DrawEdit from './draw/draw-edit';
import DrawSettings from './draw/settings/draw-settings';
import DiscussionEdit from './discussion/discussion-edit';
import DiscussionSettings from './discussion/discussion-settings';
import InteractiveResponseEdit from './interactive-response/sequence/interactive-response-edit';
import InteractiveResponseSettings from './interactive-response/sequence/settings/interactive-response-settings';
import InteractiveResponseCategoriseEdit from './interactive-response/categorise/interactive-response-categorise-edit';
import InteractiveResponseCategoriseSettings from './interactive-response/categorise/settings/interactive-response-categorise-settings';
import RiveEdit from './rive/rive-edit';
import RiveSettings from './rive/settings/rive-settings';
import GifEdit from './gif/gif-edit';
import GifSettings from './gif/gif-settings';
import ImageSphereEdit from './image-sphere/image-sphere-edit';
import ImageSphereSettings from './image-sphere/image-sphere-settings';
import {
  getAppliedColour,
  isBackgroundApplied,
  isBorderApplied,
} from './shared/component-settings-borders.utils';
import { getStyleProperties } from './components.utils';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  component: Pluck<
    GetActivitiesSectionsQuery,
    'getActivitiesSections.components'
  >;
  render: (props: {
    component: ReactNode;
    settings?: ReactNode;
    customSettingsMenu?: ReactNode;
  }) => void;
  contentId: number;
  contentType: ActivitiesContentType;
  lessonId: number;
};

const Component = ({
  component,
  render,
  contentId,
  lessonId,
  contentType,
}: Props) => {
  return (
    <ComponentFieldWrapper
      data-selector="component-wrapper"
      $colours={getAppliedColour(getStyleProperties(component))}
      $showBorder={isBorderApplied(getStyleProperties(component))}
      $showBackground={isBackgroundApplied(getStyleProperties(component))}
    >
      <>
        {component.type === ACTIVITIES_COMPONENT_TYPE_INFO_BOX &&
          render({
            component: <InfoBoxEdit {...(component as ComponentInfoBox)} />,
            settings: <InfoBoxSettings {...(component as ComponentInfoBox)} />,
          })}

        {component.type === ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE &&
          render({
            component: <ResponseEdit {...(component as ComponentResponse)} />,
            settings: (
              <ResponseSettings
                {...(component as ComponentResponse)}
                contentId={contentId}
              />
            ),
          })}

        {component.type ===
          ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE &&
          render({
            component: (
              <MultipleChoiceEdit {...(component as ComponentMultipleChoice)} />
            ),
            settings: (
              <MultipleChoiceSettings
                {...(component as ComponentMultipleChoice)}
              />
            ),
          })}

        {component.type ===
          ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE &&
          render({
            component: (
              <EquationResponseEdit
                {...(component as ComponentEquationResponse)}
                contentId={contentId}
              />
            ),
            settings: (
              <EquationResponseSettings
                {...(component as ComponentEquationResponse)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_TABLE &&
          render({
            component: <TableEdit {...(component as ComponentTable)} />,
            settings: <TableSettings {...(component as ComponentTable)} />,
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK &&
          render({
            component: (
              <EbookEdit
                {...(component as ComponentEbook)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <EbookSettings
                {...(component as ComponentEbook)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_DOCUMENT &&
          render({
            component: (
              <DocumentEdit
                {...(component as ComponentDocument)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <DocumentSettings
                {...(component as ComponentDocument)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_VIDEO &&
          render({
            component: (
              <VideoEdit
                {...(component as ComponentVideo)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <VideoSettings
                {...(component as ComponentVideo)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_AUDIO &&
          render({
            component: (
              <AudioEdit
                {...(component as ComponentAudio)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <AudioSettings
                {...(component as ComponentAudio)}
                lessonId={lessonId}
              />
            ),
          })}
        {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE &&
          render({
            component: (
              <ImageEdit
                {...(component as ComponentImage)}
                lessonId={lessonId}
                contentId={contentId}
              />
            ),
            settings: (
              <ImageSettings
                {...(component as ComponentImage)}
                lessonId={lessonId}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE &&
          render({
            component: (
              <ImageSphereEdit
                {...(component as ComponentImageSphere)}
                contentId={contentId}
              />
            ),
            settings: (
              <ImageSphereSettings
                {...(component as ComponentImageSphere)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE &&
          render({
            component: (
              <InteractiveEdit {...(component as ComponentInteractive)} />
            ),
            settings: (
              <InteractiveSettings
                {...(component as ComponentInteractive)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE &&
          render({
            component: (
              <InteractiveEbookEdit
                {...(component as ComponentEbookInteractive)}
              />
            ),
            settings: (
              <InteractiveEbookSettings
                {...(component as ComponentEbookInteractive)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_COLUMNS &&
          render({
            component: (
              <ColumnsEdit
                {...(component as ComponentColumns)}
                contentId={contentId}
                lessonId={lessonId}
                contentType={contentType}
              />
            ),
            settings: <ColumnsSettings {...(component as ComponentColumns)} />,
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_LINK &&
          render({
            component: (
              <LinkEdit {...(component as ComponentLink)} lessonId={lessonId} />
            ),
            settings: (
              <LinkSettings
                {...(component as ComponentLink)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_WEBTOOL &&
          render({
            component: (
              <WebToolEdit
                {...(component as ComponentLink)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <WebToolSettings
                {...(component as ComponentLink)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH &&
          render({
            component: (
              <GuidedResearchEdit
                {...(component as ComponentGuidedResearch)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <GuidedResearchSettings
                {...(component as ComponentGuidedResearch)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_SLIDESHOW &&
          render({
            component: (
              <SlideshowEdit
                {...(component as ComponentSlideshow)}
                contentId={contentId}
              />
            ),
            settings: (
              <SlideshowSettings
                {...(component as ComponentSlideshow)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_VOCABULARY &&
          render({
            component: (
              <VocabularyEdit {...(component as ComponentVocabulary)} />
            ),
            settings: (
              <VocabularySettings
                {...(component as ComponentVocabulary)}
                contentId={contentId}
                contentType={contentType}
              />
            ),
          })}

        {component.type ===
          ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE &&
          render({
            component: (
              <FillInBlanksEdit {...(component as ComponentFillInBlanks)} />
            ),
            settings: (
              <FillInBlanksSettings {...(component as ComponentFillInBlanks)} />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_DRAW &&
          render({
            component: <DrawEdit {...(component as ComponentDraw)} />,
            settings: (
              <DrawSettings
                {...(component as ComponentDraw)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE &&
          render({
            component: (
              <DiscussionEdit {...(component as ComponentDiscussion)} />
            ),
            settings: (
              <DiscussionSettings {...(component as ComponentDiscussion)} />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE &&
          render({
            component: (
              <InteractiveResponseEdit
                {...(component as ComponentInteractiveResponseSequence)}
              />
            ),
            settings: (
              <InteractiveResponseSettings
                {...(component as ComponentInteractiveResponseSequence)}
              />
            ),
          })}

        {component.type ===
          ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE &&
          render({
            component: (
              <InteractiveResponseCategoriseEdit
                {...(component as ComponentInteractiveResponseCategorise)}
              />
            ),
            settings: (
              <InteractiveResponseCategoriseSettings
                {...(component as ComponentInteractiveResponseCategorise)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION &&
          render({
            component: <RiveEdit {...(component as ComponentRiveAnimation)} />,
            settings: (
              <RiveSettings {...(component as ComponentRiveAnimation)} />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_GIF &&
          render({
            component: <GifEdit {...(component as ComponentGif)} />,
            settings: <GifSettings {...(component as ComponentGif)} />,
          })}
      </>
    </ComponentFieldWrapper>
  );
};

export default Component;
